import type { ReactNode } from 'react';
import { Icon } from './Icon.js';
import { iconSearch } from './generated/icons.js';
import { cx } from './util.js';

/** The five warm and cool photo placeholders of the design, picked from any string so a property keeps its tone. */
export function toneOf(seed: string): number {
  let sum = 0;
  for (const ch of seed) sum = (sum + ch.charCodeAt(0)) % 5;
  return sum;
}

export interface ArchThumbProps {
  /** The photo. Without one, a soft placeholder tone is drawn. */
  src?: string | null;
  /** Spoken text for the photo; empty for a purely decorative one. */
  alt?: string;
  /** Picks the placeholder tone. */
  seed?: string;
}

/** A 56 by 66 arch: the shape imagery takes in a list row. */
export function ArchThumb({ src, alt = '', seed = '' }: ArchThumbProps) {
  return (
    <span className={cx('k-thumb', src == null && `k-photo--p${String(toneOf(seed))}`)}>
      {src == null ? null : <img src={src} alt={alt} loading="lazy" />}
    </span>
  );
}

export interface TagProps {
  tone?: 'neutral' | 'ok' | 'warn' | 'err';
  children: ReactNode;
}

/** A quiet status: the word carries the state and a small dot repeats it. */
export function Tag({ tone = 'neutral', children }: TagProps) {
  return <span className={cx('k-tag k-t-label-sm', `k-tag--${tone}`)}>{children}</span>;
}

export interface SearchFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

/** The pill search field above a list. */
export function SearchField({ label, placeholder, value, onChange }: SearchFieldProps) {
  return (
    <label className="k-search">
      <Icon icon={iconSearch} />
      <span className="k-sr-only">{label}</span>
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** The docked bar of a screen's one or two actions, above the bottom bar. Buttons share the width. */
export function ActionBar({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="k-dock" role="group" aria-label={label}>
      {children}
    </div>
  );
}
